import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useStoreState } from '../../models/RootStore';
import { isEmpty, isNil, omitBy } from 'lodash';
import { t } from 'ttag';
import { minsToTime, timeStringToMins } from '../../services/timeService';
import {
  mergeUiTemplatePointsWithParamTemplate,
  getSupportedControlTypes,
  controlableTypes,
  controlTypes,
} from '../../screens/Control/ControlComponents/BacnetSchedule/constants';
const DEFAULT_POWER_ON_TIME = '00:00';
/**
 * Hook for managing BACnet schedule form state and validation
 * Handles form data, validation, and submission logic
 */
const useBacnetScheduleForm = (
  bacnetUnit,
  initialScheduleData = {},
  externalUiTemplate = null,
  externalParamTemplate = null
) => {
  const editMode = !isEmpty(initialScheduleData);

  // Store state
  const user = useStoreState((state) => state.users?.me || {});
  const timeFormat = useStoreState((state) => state.users?.timeFormat || '24');
  const types = useStoreState((state) => state.types || {});
  const { timeFormat: timeFormatTypes, weekDays: weekDaysTypes = {}, scheduleCategories } = types;
  const { timeFormat: myTimeFormat } = user;

  // Form state
  const [scheduleName, setScheduleName] = useState('');
  const [scheduleDisabled, setScheduleDisabled] = useState(false);
  const [powerOnTime, setPowerOnTime] = useState(DEFAULT_POWER_ON_TIME);
  const [days, setDays] = useState([]);
  const [scheduleObjects, setScheduleObjects] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [dirty, setDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [dates, setDates] = useState([]);
  const [tab, setTab] = useState(scheduleCategories.weekly);

  // Error message timeout ref
  const errorTimeoutRef = useRef(null);

  // Template state - use external templates if provided
  const [uiTemplate, setUiTemplate] = useState(externalUiTemplate);
  const [paramTemplate, setParamTemplate] = useState(externalParamTemplate);

  // Update templates when external templates change
  useEffect(() => {
    if (
      externalUiTemplate !== null &&
      externalUiTemplate !== undefined &&
      externalUiTemplate !== uiTemplate
    ) {
      setUiTemplate(externalUiTemplate);
    }
  }, [externalUiTemplate, uiTemplate]);

  useEffect(() => {
    if (
      externalParamTemplate !== null &&
      externalParamTemplate !== undefined &&
      externalParamTemplate !== paramTemplate
    ) {
      setParamTemplate(externalParamTemplate);
    }
  }, [externalParamTemplate, paramTemplate]);

  // Time format utilities
  const timeFormatObject = myTimeFormat
    ? timeFormatTypes[myTimeFormat]
    : timeFormatTypes[0];
  const is12Hours = timeFormatObject?.text === '12 hours';
  const weekDaysArray = Object.keys(weekDaysTypes);

  // Helper functions
  const hasValue = useCallback((value) => {
    return !!value || value === 0;
  }, []);

  // Set error message with auto-reset
  const setErrorMsgWithTimeout = useCallback((msg) => {
    // Clear any existing timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    // Set the error message
    setErrorMsg(msg);

    // Set timeout to clear the error
    if (msg) {
      errorTimeoutRef.current = setTimeout(() => {
        setErrorMsg('');
        errorTimeoutRef.current = null;
      }, 3500);
    }
  }, []);

  const isValidValue = useCallback((obj) => {
    const { value, config, controlType } = obj;
    if (isNil(value)) {
      return false;
    }

    if (obj?.config?.customEnumVisibility) {
      return Object.prototype.hasOwnProperty.call(
        obj.config.customEnumVisibility,
        obj.value
      );
    }

    if (controlType != controlTypes.numericValue) {
      return true;
    }

    // Handle numeric values with customEnum constraints
    const numValue = Number(value);
    if (config?.customEnum) {
      const min = config?.customEnum.min;
      const max = config?.customEnum.max;
      return numValue >= min && numValue <= max;
    }

    return true;
  }, []);

  // Merged data points from templates
  const mergedDataPoints = useMemo(() => {
    // Try to merge templates first
    if (paramTemplate?.data && uiTemplate?.data?.points) {
      const merged =
        mergeUiTemplatePointsWithParamTemplate(
          paramTemplate.data,
          uiTemplate.data.points
        ) || {};
      return omitBy(merged, (point) => point?.config?.readOnly);
    }

    // Universal (template-less) units have no uiTemplate — build the control
    // list directly from the raw paramTemplate points instead
    if (paramTemplate?.data) {
      const points = {};
      Object.entries(paramTemplate.data).forEach(([pointId, pointConfig]) => {
        if (!pointConfig || pointConfig.readOnly) {
          return;
        }

        const supportedControls = getSupportedControlTypes(pointConfig, pointId);
        const controlType = supportedControls.find(
          (type) => controlableTypes[type]
        );
        if (!controlType) {
          return;
        }

        points[pointId] = {
          label: pointConfig.name || pointId,
          controlType,
          config: { ...pointConfig, supportedControls },
        };
      });
      return points;
    }

    // Fallback: use BACnet unit data points if templates are not available
    if (bacnetUnit?.data) {
      const fallbackPoints = {};
      Object.entries(bacnetUnit.data).forEach(([key, point]) => {
        if (point && !point.readOnly) {
          fallbackPoints[key] = {
            name: point.name || key,
            config: point,
            value: point.value,
          };
        }
      });
      return fallbackPoints;
    }

    return {};
  }, [paramTemplate, uiTemplate, bacnetUnit]);

  // Schedule objects payload for submission
  const scheduleObjectsPayload = useMemo(
    () =>
      Object.entries(scheduleObjects).map(([object, obj]) => ({
        object,
        value: obj.value,
      })),
    [scheduleObjects]
  );

  // Validation
  const isFormValid = useMemo(() => {
    if (!scheduleName.trim()) return false;
    if (!powerOnTime) return false;
    if (days.length === 0) return false;
    if (Object.keys(scheduleObjects).length === 0) return false;

    // Check if any object has invalid value
    const hasInvalidObject = Object.values(scheduleObjects).some(
      (obj) => !isValidValue(obj)
    );
    if (hasInvalidObject) return false;

    return true;
  }, [scheduleName, powerOnTime, days, scheduleObjects, isValidValue]);

  // Initialize form with schedule data
  const initializeForm = useCallback(
    (scheduleData) => {
      const {
        name,
        powerOnTime: schedulePowerOnTime,
        days: scheduleDays = [],
        dates: scheduleDates = [],
        isDisabled,
        bacnetObjects = [],
        scheduleCategory,
      } = scheduleData || {};

      setScheduleDisabled(isDisabled || false);
      setPowerOnTime(
        hasValue(schedulePowerOnTime)
          ? minsToTime(schedulePowerOnTime, timeFormat)
          : ''
      );
      setScheduleName(name || '');
      setDays(scheduleDays);
      const selectedDates = scheduleDates.map((date) => new Date(date));
      setDates(selectedDates);
      setTab(scheduleCategory);

      // Initialize schedule objects
      const initialObjects = bacnetObjects.reduce((acc, item) => {
        const { object, value } = item || {};
        if (!object) return acc;

        const point = mergedDataPoints?.[object] || {};
        acc[object] = {
          id: object,
          ...point,
          value,
        };
        return acc;
      }, {});

      setScheduleObjects(initialObjects);
      setDirty(false);
      setErrorMsg('');
    },
    [hasValue, timeFormat, mergedDataPoints]
  );

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setScheduleName('');
    setScheduleDisabled(false);
    setPowerOnTime('');
    setDays([]);
    setDates([]);
    setTab(scheduleCategories.weekly);
    setScheduleObjects({});
    setErrorMsg('');
    setDirty(false);
    setIsSubmitting(false);
    setIsInitialized(false);
  }, []);

  // Update schedule object value
  const updateScheduleObject = useCallback((objectId, value) => {
    setScheduleObjects((prev) => {
      // Prevent unnecessary updates if value hasn't changed
      if (prev[objectId]?.value === value) {
        return prev;
      }

      const newState = {
        ...prev,
        [objectId]: {
          ...prev[objectId],
          value,
        },
      };

      return newState;
    });

    setDirty(true);
  }, []);

  // Add schedule object
  const addScheduleObject = useCallback((objectId, objectData) => {
    setScheduleObjects((prev) => ({
      ...prev,
      [objectId]: {
        id: objectId,
        ...objectData,
        value: objectData.value !== undefined ? objectData.value : '',
      },
    }));
    setDirty(true);
  }, []);

  // Remove schedule object
  const removeScheduleObject = useCallback((objectId) => {
    setScheduleObjects((prev) => {
      // eslint-disable-next-line no-unused-vars
      const { [objectId]: removed, ...remaining } = prev;
      return remaining;
    });
    setDirty(true);
  }, []);

  // Update schedule object properties (not value)
  const updateScheduleObjectProperties = useCallback((objectId, properties) => {
    setScheduleObjects((prev) => ({
      ...prev,
      [objectId]: {
        ...prev[objectId],
        ...properties,
      },
    }));
    setDirty(true);
  }, []);

  // Validate form data
  const validateForm = useCallback(() => {
    if (!scheduleName.trim()) {
      setErrorMsgWithTimeout(t`Schedule name is required`);
      return false;
    }

    if (scheduleName.length < 3 || scheduleName.length > 25) {
      setErrorMsgWithTimeout(t`Schedule Name should be from 3 to 25 chars`);
      return false;
    }

    if((tab == scheduleCategories.weekly) && days.length === 0){
      setErrorMsgWithTimeout(t`At least one day must be selected`);
      return false;
    }

    if((tab == scheduleCategories.calendar) && dates.length === 0){
      setErrorMsgWithTimeout(t`At least one date must be selected`);
      return false;
    }

    if (!powerOnTime) {
      setErrorMsgWithTimeout(t`Start Time is Required`);
      return false;
    }

    if (Object.keys(scheduleObjects).length === 0) {
      setErrorMsgWithTimeout(t`Select at least one Control object`);
      return false;
    }

    // Validate schedule objects
    const invalidObj = Object.values(scheduleObjects).find((obj) => {
      return !isValidValue(obj);
    });

    if (invalidObj) {
      setErrorMsgWithTimeout(
        t`Invalid value for ` + (invalidObj.label ?? invalidObj.config?.name)
      );
      return false;
    }

    setErrorMsg('');
    return true;
  }, [scheduleName, powerOnTime, days, scheduleObjects, tab, dates, isValidValue]);

  // Prepare form data for submission
  const getFormData = useCallback(() => {
    if (!validateForm()) {
      return null;
    }

    const startHour = timeStringToMins(powerOnTime);

    const data = {
      isDisabled: scheduleDisabled,
      powerOnTime: startHour,
      name: scheduleName,
      bacnetObjects: scheduleObjectsPayload,
      scheduleCategory: tab,
    };
    if (tab === scheduleCategories.calendar) {
      data.dates = dates;
    }
    if (tab === scheduleCategories.weekly) {
      data.days = days;
    }
    return data;
  }, [
    validateForm,
    scheduleDisabled,
    powerOnTime,
    timeFormat,
    scheduleName,
    days,
    dates,
    tab,
    scheduleObjectsPayload,
  ]);

  // Handle form field changes
  const handleFieldChange = useCallback((field, value) => {
    setDirty(true);

    switch (field) {
      case 'name':
        setScheduleName(value);
        break;
      case 'disabled':
        setScheduleDisabled(value);
        break;
      case 'powerOnTime':
        setPowerOnTime(value);
        break;
      case 'days':
        setDays(value);
        break;
      case 'dates':
        setDates(value);
        break;
      case 'tab':
        setTab(value);
        break;
      default:
        break;
    }
  }, []);

  // Initialize form when schedule data is available (only once in edit mode)
  useEffect(() => {
    if (
      editMode &&
      initialScheduleData &&
      Object.keys(mergedDataPoints).length > 0 &&
      !isInitialized
    ) {
      initializeForm(initialScheduleData);
      setIsInitialized(true);
    }
  }, [
    editMode,
    initialScheduleData,
    mergedDataPoints,
    initializeForm,
    isInitialized,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  return {
    // Form data
    scheduleName,
    scheduleDisabled,
    powerOnTime,
    days,
    dates,
    tab,
    scheduleObjects,
    scheduleObjectsPayload,

    // Templates
    uiTemplate,
    paramTemplate,
    mergedDataPoints,

    // State
    editMode,
    dirty,
    isSubmitting,
    errorMsg,
    isFormValid,

    // Time format utilities
    is12Hours,
    weekDaysArray,
    timeFormatObject,

    // Actions
    setScheduleName,
    setScheduleDisabled,
    setPowerOnTime,
    setDays,
    setDates,
    setTab,
    setUiTemplate,
    setParamTemplate,
    setIsSubmitting,
    setErrorMsg,
    setErrorMsgWithTimeout,

    // Object management
    updateScheduleObject,
    updateScheduleObjectProperties,
    addScheduleObject,
    removeScheduleObject,
    setScheduleObjects,

    // Form utilities
    initializeForm,
    resetForm,
    validateForm,
    getFormData,
    handleFieldChange,
    hasValue,

    // Setters for external control
    setDirty,
  };
};

export default useBacnetScheduleForm;
